import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { ContentLoading, ErrorPage } from 'axway-react-ui';
import FlowGraph from 'axway-flow-graph';
import qs from 'qs';
import browserHistory from 'components/utility/history';
import {
	buildFlowInputsMap,
	buildTriggerParameters,
	getNewTriggerStatusMessage
} from 'components/pages/flows/utils';
import * as actions from '../actions';
import FlowView from '../view/FlowView';

const LEGACY_SPEC_TYPE = 'endpoint';

class FlowContainer extends React.Component {
	constructor(props) {
		super(props);
		this.onClose = this.onClose.bind(this);
		this.onSave = this.onSave.bind(this);
		this.onApply = this.onApply.bind(this);
		this.onChange = this.onChange.bind(this);
		this.onDelete = this.onDelete.bind(this);
		this.startDebug = this.startDebug.bind(this);
		this.clearDebug = this.clearDebug.bind(this);
		this.prevLocation = this._getPreviousLocation();
		this.selectionDetails = null;
	}

	_getPreviousLocation() {
		const { prevLocation } = this.props;
		// Redirect to previous location if it was within /project/apidocs/...
		const rx = /.*\/project\/apidocs\/.*/;
		if (prevLocation && rx.test(prevLocation.pathname)) {
			return prevLocation.pathname;
		} else {
			return `${window.$conf.prefix}/project/flows`;
		}
	}

	startDebug(flow, params, options) {
		this.props.actions.debugFlowStart(flow, params, options);
	}

	clearDebug() {
		this.props.actions.debugFlowClear();
	}

	onClose() {
		browserHistory.push(this.prevLocation);
	}

	onChange(flow) {
		this.props.actions.validateFlow(flow);
	}

	onDelete() {
		this.props.actions.deleteFlow(this.flowId, this.prevLocation);
	}

	doSaveAction(flow, redirect) {
		if (!this.props.isNewFlow) {
			this.props.actions.saveFlow(this.flowId, flow, redirect);
		} else if (this.props.flowType === 'endpoint') {
			this.props.actions.createEndpointFlow(
				this.props.group,
				this.props.method,
				this.props.path,
				this.flowId,
				flow,
				redirect,
				this.props.projectName
			);
		} else {
			// Fire the create unbound flow action
			this.props.actions.createUnboundFlow(
				this.flowId,
				flow,
				redirect,
				this.props.projectName
			);
		}
	}

	onSave(flow) {
		this.doSaveAction(flow, this.prevLocation);
	}

	onApply(flow, selectionDetails = null) {
		this.selectionDetails = selectionDetails;
		this.doSaveAction(flow);
	}

	render() {
		const {
			isLoading,
			nodehandlers,
			triggerDefinitions,
			flowValidation,
			flow,
			debug,
			credentials,
			projectName
		} = this.props;

		if (this.flowGraph) {
			this.flowGraph = null;
			this.flowId = null;
		}

		if (this.props.isNotFound) {
			return (
				<ErrorPage errorDetail={flow.errorDetail} />
			);
		} else if (isLoading) {
			return (
				<ContentLoading />
			);
		} else {
			if (flow && flow.flow && flow.flowId) {
				// Has the flow changed?
				if (!this.flowGraph || (flow.flowId !== this.flowId)) {
					this.flowGraph = new FlowGraph(flow.flowId, flow.flow);
					this.flowId = flow.flowId;
				}
			}

			const flowSaving = {
				saving: flow && flow.saving,
				saveError: flow && flow.saveError,
				saveErrorDetail: flow && flow.saveErrorDetail
			};

			return (
				<FlowView
					flowId={this.flowId}
					projectName={projectName}
					debug={debug}
					startDebug={this.startDebug}
					clearDebug={this.clearDebug}
					onClose={this.onClose}
					onSave={this.onSave}
					onApply={this.onApply}
					onChange={this.onChange}
					onDelete={this.onDelete}
					flow={this.flowGraph}
					flowTriggers={flow.flowTriggers}
					triggerDefinitions={triggerDefinitions}
					nodehandlers={nodehandlers}
					schemas={this.props.schemas}
					flowValidation={flowValidation}
					flowSaving={flowSaving}
					isNewFlow={this.props.isNewFlow}
					isTriggeredBySpec={this.props.isTriggeredBySpec}
					specFlowTriggerTypeIds={this.props.specFlowTriggerTypeIds}
					selectedNode={this.selectionDetails}
					credentials={credentials}
				/>
			);
		}
	}
}

function mapStateToProps(state, ownProps) {
	const isNew = !ownProps.match.params.id;
	const flowId = isNew ? '$$_new_flow' : decodeURIComponent(ownProps.match.params.id);
	const flow = state.flows.flows[flowId];
	const debug = state.flows.debug || {};
	const triggerDefinitions = state.flows.triggerDefinitions
		&& state.flows.triggerDefinitions.definitions || {};
	const isLoading = !flow
		|| flow.loading
		|| state.apidocs.loading
		|| (state.flows.triggerDefinitions && state.flows.triggerDefinitions.loading);
	let specFlowTriggerTypeIds = [];
	let isTriggeredBySpec = false;

	// Gather the spec flow-trigger type ids and if the flow is triggered or not
	if (!isLoading) {
		// Gather the spec flow-trigger type ids
		if (state.apidocs && state.apidocs.apis) {
			specFlowTriggerTypeIds = state.apidocs.apis
				.filter(a => a.type !== LEGACY_SPEC_TYPE)
				.map(a => a.id);
		}
		// Find if the flow is triggered by a spec. New flows are handled below.
		if (!isNew && !flow.error) {
			const triggeredBy = Object.keys(flow.flowTriggers.triggers);
			isTriggeredBySpec
				= !!triggeredBy.find(id => specFlowTriggerTypeIds.includes(id));
		}
	}

	if (isNew) {
		let query = {};
		if (!ownProps.location || !ownProps.location.search) {
			// TODO(MP): We should consider flagging the flow in an error state.
			// If we don't have the location details, we can't do anything and
			// saving will fail. Warning is not enough.
			// eslint-disable-next-line no-console
			console.warn('FlowContainer expected a location URL with query parameters based on the type of flow to be created: method, path, and group for Endpoint flows and friendlyName for Unbound flows.');
		} else {
			query = qs.parse(ownProps.location.search, { ignoreQueryPrefix: true });
		}

		const { triggerTypeId, method, path } = query;

		// A flow is considered to be of a Trigger Type when it contains
		// a triggerType ID. This is the new way of creating flows based
		// on different type of protocols.
		const isTriggerType = !!triggerTypeId;

		// Going forward we are going to have two types of flows:
		//   'endpoint' - which is the legacy oas flows
		//   'ubound', which is the new triggers way of handling things.
		// The type controls multiple things - i.e how the flow is saved
		const flowType = query.friendlyName || isTriggerType ? 'unbound' : 'endpoint';

		// If it is a new endpoint flow, we need to manually add an endpoint
		// flow-trigger as one does not yet exist on disk(we create it after
		// the flow is successfully saved). This is now the legacy mode and will
		// exist only for old apps.
		if (flowType === 'endpoint') {
			if (method && path) {
				flow.flowTriggers = {
					triggers: {},
					endpoints: [{
						// it will switch once it is saved
						enabled: false,
						status: {
							valid: true
						},
						summary: `${method.toUpperCase()} ${query.path}`
					}]
				};
			}

		// The same concept as the old legacy code above, but it creates an
		// trigger in memory, just until it is saved on disk.
		} else if (isTriggerType) {
			// TODO(MP): This is now defined into places, the utility function and the FlowView.
			// We would ideally want to intl this, however this container doesn't have that ability.
			const newTriggerStatusMsg = getNewTriggerStatusMessage();

			const definition = triggerDefinitions[triggerTypeId] || {};
			const { parameter = {} } = flow.flow;
			const flowInputsMap = buildFlowInputsMap(
				parameter,
				definition.requestSchema
			);

			const { triggerParameters = {} } = definition;
			const params = buildTriggerParameters(
				triggerParameters,
				query
			);

			// We don't need this to be an ugly uuid. We are hiding it in the UI
			// based on the trigger status message above and it will be unique
			// per flow creation. Dropping a new trigger of this type uses
			// a guid so it won't clash. Once the trigger is saved on disk we
			// pick the friendly id based on what triggers exist. This
			// is just a temporary ID.
			const triggerId = `${triggerTypeId}-1`;
			isTriggeredBySpec = specFlowTriggerTypeIds.includes(triggerTypeId);
			flow.flow.triggers = {
				[triggerTypeId]: {
					[triggerId]: {
						enabled: false, // it will switch once it is saved
						status: {
							valid: true,
							error: newTriggerStatusMsg // hides the id in the UI
						},
						triggerParameters: params,
						flowId,
						flowInputsMap,
						instanceName: method
					}
				}
			};
		}

		// new flow
		return {
			isLoading,
			isNewFlow: true,
			isNotFound: false,
			isTriggeredBySpec,
			specFlowTriggerTypeIds,
			flow,
			debug,
			saveComplete: flow && flow.saveComplete,
			nodehandlers: state.flows.nodehandlers,
			triggerDefinitions,
			prevLocation: state.location.prevLocation,
			schemas: state.schema.schemas,
			flowValidation: state.flows.flowValidation,
			method: query.method,
			path: query.path,
			group: query.group,
			flowType,
			credentials: state.credentials
				&& state.credentials.credentials || {},
			projectName: (state.project && state.project.project
				&& state.project.project.name) || ''
		};
	}

	if (flow && flow.error && flow.errorDetail && flow.errorDetail.status === 404) {
		return {
			isLoading,
			isNotFound: true,
			isNewFlow: false,
			isTriggeredBySpec,
			specFlowTriggerTypeIds,
			flow: flow,
			debug,
			nodehandlers: state.flows.nodehandlers,
			prevLocation: state.location.prevLocation,
			schemas: state.schema.schemas
		};
	}

	return {
		isLoading,
		isNewFlow: false,
		isNotFound: false,
		isTriggeredBySpec,
		specFlowTriggerTypeIds,
		apidocs: state.apidocs,
		flow: flow,
		debug,
		saveComplete: flow && flow.saveComplete,
		nodehandlers: state.flows.nodehandlers,
		triggerDefinitions,
		prevLocation: state.location.prevLocation,
		schemas: state.schema.schemas,
		flowValidation: state.flows.flowValidation,
		credentials: state.credentials
			&& state.credentials.credentials || {},
		projectName: (state.project && state.project.project
			&& state.project.project.name) || ''
	};
}

function mapDispatchToProps(dispatch) {
	return {
		actions: bindActionCreators(actions, dispatch)
	};
}

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(FlowContainer);
